import { uniq } from 'lodash';
import { SectionSyncRuleDto } from '../types';
import { SectionModel, InputBaseModel } from '../models';
import { InstanceUtils } from '.';
import { TypeUtils } from '../../common/misc';

export default class SectionSyncManager {
    private readonly syncRules: SectionSyncRuleDto[];
    private readonly sectionProvider: (input: InputBaseModel) => SectionModel[];

    constructor(syncRules: SectionSyncRuleDto[] | null, sectionProvider: (input: InputBaseModel) => SectionModel[]) {
        this.syncRules = syncRules ?? [];
        this.sectionProvider = sectionProvider;
    }

    get hasSyncRules(): boolean {
        return this.syncRules.length > 0;
    }

    processSyncRules(input: InputBaseModel): void {
        if (!this.hasSyncRules) {
            return;
        }

        this.syncRules.filter(rule => rule.inputId === input.id).forEach(rule => this.applySyncRule(input, rule));
    }

    markReadonlyInputs(sectionId: string, inputs: InputBaseModel[]) {
        if (!this.hasSyncRules) {
            return;
        }

        const readonlyInputIds = new Set(
            this.syncRules.filter(rule => rule.targetSectionId === sectionId).map(rule => rule.inputId)
        );

        inputs.filter(input => readonlyInputIds.has(input.id)).forEach(input => input.setIsReadOnly(true));
    }

    private applySyncRule(input: InputBaseModel, rule: SectionSyncRuleDto): void {
        const sections = this.sectionProvider(input);
        const inputSection = this.getSectionByInputGuid(sections, input.guid);

        if (!inputSection || inputSection.id === rule.targetSectionId) {
            return;
        }

        const targetSection = this.getSectionById(sections, rule.targetSectionId);
        const targetInput = targetSection?.getInputById(rule.inputId);

        if (targetSection && targetInput) {
            this.syncInputValues(targetInput, targetSection, sections);
        }
    }

    private syncInputValues(targetInput: InputBaseModel, targetSection: SectionModel, sections: SectionModel[]): void {
        const syncedValues = uniq(this.getValuesFromSections(sections, targetInput.id, targetSection.id));

        const currentValues = TypeUtils.isStringArray(targetInput.value) ? targetInput.value : [];

        if (!this.hasValueChanged(currentValues, syncedValues)) {
            return;
        }

        targetInput.setValue(syncedValues);

        if (InstanceUtils.isInputWithSource(targetInput)) {
            targetInput.fetchSourceOptionsHierarchy();
        }
    }

    private getValuesFromSections(sections: SectionModel[], inputId: string, excludeSectionId: string): string[] {
        return sections
            .filter(section => section.id !== excludeSectionId)
            .flatMap(section => {
                const input = section.getInputById(inputId);
                return input && TypeUtils.isStringArray(input.value) ? input.value : [];
            });
    }

    private hasValueChanged(currentValues: string[], newValues: string[]): boolean {
        return currentValues.length !== newValues.length || !newValues.every(value => currentValues.includes(value));
    }

    private getSectionById(sections: SectionModel[], id: string): SectionModel | undefined {
        return sections.find(section => section.id === id);
    }

    private getSectionByInputGuid(sections: SectionModel[], guid: string): SectionModel | undefined {
        return sections.find(section => section.inputs.some(input => input.guid === guid));
    }
}
